import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/services/apiClient';
import type { HospitalConfig } from '@/services/api/systemSettingsApi';
import { externalProvidersApi } from '@/services/api/externalProvidersApi';
import { useAuthStore } from '@/stores/useAuthStore';

/**
 * WHAT SYSTEM SETTINGS READS AND WRITES, and why it is not the endpoints the
 * legacy admin screen uses.
 *
 * The Clinic Manager holds `hospital_settings:read` / `hospital_settings:write`
 * and nothing else about settings. The two obvious write paths refuse them:
 *
 *   PUT  /system-settings/frontend/hospital-config  → 403, needs `system:write`
 *   PATCH /tenants/:id/branding                     → 403, OWNER_REQUIRED
 *
 * Both were verified against the live API with this role's own token. So the
 * backend carries a second door with the right gate on it — `clinic-settings`
 * (MAIE.Backend, `src/clinic-settings/`) — which delegates to the same two
 * services and takes the tenant from the request's context. That is what these
 * hooks read and write.
 *
 * The reads still come back through the SAME `{success, data}` envelope every
 * other endpoint uses, unwrapped here and never in a component (BRIEF §4.4).
 */

export interface BrandingRow {
  tenantId: string;
  displayName: string | null;
  logoUrl: string | null;
  logoDarkUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  accentColor: string | null;
  themeId: string | null;
  themeIdDark: string | null;
  themeConfig: Record<string, unknown> | null;
  themeConfigDark: Record<string, unknown> | null;
  /** The sign-in hero's own pair — never the in-app theme (boards 15/17). */
  loginPrimaryColor: string | null;
  loginAccentColor: string | null;
  /** The clinic-wide document template every printed artifact is laid out on. */
  pdfTemplate: string | null;
  pdfAccentColor: string | null;
}

export interface BrandingPatchInput {
  displayName?: string;
  logoUrl?: string | null;
  faviconUrl?: string | null;
  primaryColor?: string | null;
  accentColor?: string | null;
  themeId?: string | null;
  themeIdDark?: string | null;
  themeConfig?: Record<string, unknown> | null;
  themeConfigDark?: Record<string, unknown> | null;
  loginPrimaryColor?: string | null;
  loginAccentColor?: string | null;
  pdfTemplate?: string | null;
  pdfAccentColor?: string | null;
}

const unwrap = <T>(body: unknown): T =>
  ((body as { data?: T })?.data ?? body) as T;

export function useFacility() {
  const q = useQuery({
    queryKey: ['cm-facility'],
    staleTime: 300_000,
    retry: false,
    queryFn: async (): Promise<HospitalConfig | null> => {
      const res = await apiClient.get('/clinic-settings/facility');
      return unwrap<HospitalConfig | null>(res.data);
    },
  });
  return { ...q, facility: q.data ?? null };
}

export function useSaveFacility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (config: HospitalConfig) => {
      const res = await apiClient.put('/clinic-settings/facility', config);
      return unwrap<HospitalConfig>(res.data);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cm-facility'] });
    },
  });
}

export function useBranding() {
  const q = useQuery({
    queryKey: ['cm-branding'],
    staleTime: 300_000,
    retry: false,
    queryFn: async (): Promise<BrandingRow | null> => {
      const res = await apiClient.get('/clinic-settings/branding');
      return unwrap<BrandingRow | null>(res.data);
    },
  });
  return { ...q, branding: q.data ?? null };
}

export function useSaveBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: BrandingPatchInput) => {
      const res = await apiClient.put('/clinic-settings/branding', patch);
      return unwrap<BrandingRow>(res.data);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cm-branding'] });
    },
  });
}

/**
 * The Directory door's headline — "41 external providers".
 *
 * The registry itself belongs to the Directory surface; this counts it, which
 * is all a door needs to say whether it is worth opening.
 */
export function useProviderCount() {
  const q = useQuery({
    queryKey: ['cm-external-providers-count'],
    staleTime: 300_000,
    retry: false,
    queryFn: async () => (await externalProvidersApi.list()).length,
  });
  return q.data ?? null;
}

/**
 * The tenant's plan, for the Subscription door.
 *
 * `/me/billing/subscription` answers 403 for a Clinic Manager — money is the
 * owner's and the accountant's — so this resolves to `null` and the door says
 * so instead of printing a plan nobody told it about. `retry: false` keeps a
 * known refusal from being asked three times on every mount.
 */
export function useSubscriptionLine() {
  /* DON'T ASK A QUESTION WHOSE ANSWER YOU ALREADY KNOW. `retry: false` stopped
     the refusal being asked three times, but a Clinic Manager still spent one
     guaranteed 403 (OWNER_REQUIRED) on every mount of the Settings home. The
     session already says whose clinic this is, so only the owner asks; for
     everyone else the door reads refused without a request. */
  const isOwner = useAuthStore((s) => s.activeTenant?.role) === 'Owner';
  const q = useQuery({
    queryKey: ['cm-subscription-line'],
    staleTime: 600_000,
    retry: false,
    enabled: isOwner,
    queryFn: async () => {
      const res = await apiClient.get('/me/billing/subscription');
      return unwrap<{ tier?: string; currentPeriodEnd?: string | null } | null>(res.data);
    },
  });
  return { plan: q.data ?? null, refused: !isOwner || q.isError };
}

/* ==================================================================== *
 * THE SIX ROOMS BATCH 3 ADDS.
 *
 * Every one of them reads and writes `/clinic-settings/*` — the door with
 * `hospital_settings:read|write` on it — for the reason the header of this
 * file gives: the endpoints these settings already had are gated on
 * `system:write`, a platform permission nobody inside a clinic holds. Each
 * hook unwraps the envelope here so no component ever sees it (§4.4).
 * ==================================================================== */

function reader<T>(key: string, path: string, staleTime = 120_000) {
  return function useReader() {
    const q = useQuery({
      queryKey: [key],
      staleTime,
      /* A dropped connection (ERR_NETWORK_CHANGED on a laptop that roamed) is not
         an answer — try again by itself. A 4xx is an answer and is not retried. */
      retry: (count, err) => {
        const status = (err as { response?: { status?: number } })?.response?.status;
        return count < 2 && (status === undefined || status >= 500);
      },
      retryDelay: 600,
      queryFn: async (): Promise<T | null> => {
        const res = await apiClient.get(path);
        return unwrap<T | null>(res.data);
      },
    });
    return { ...q, value: q.data ?? null };
  };
}

function writer<TIn, TOut>(key: string, path: string) {
  return function useWriter() {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async (body: TIn) => {
        const res = await apiClient.put(path, body);
        return unwrap<TOut>(res.data);
      },
      onSuccess: () => { void qc.invalidateQueries({ queryKey: [key] }); },
    });
  };
}

/* ---- Recording (board 20) ---- */
export interface RecordingLimits {
  maxRecordingMinutes: number;
  silenceTimeoutMinutes: number;
}
export const useRecordingLimits = reader<RecordingLimits>('cm-recording', '/clinic-settings/recording-limits');
export const useSaveRecordingLimits = writer<RecordingLimits, RecordingLimits>('cm-recording', '/clinic-settings/recording-limits');

/* ---- Patient IDs (board 21) ---- */
export interface PatientIdFormat {
  prefix: string;
  strategy: 'sequential' | 'random';
  /** The next number the auto-MRN would issue; null for the random strategy. */
  nextMrn: string | null;
}
export const usePatientIdFormat = reader<PatientIdFormat>('cm-mrn', '/clinic-settings/patient-id-format');
export const useSavePatientIdFormat = writer<{ prefix: string; strategy: string }, PatientIdFormat>('cm-mrn', '/clinic-settings/patient-id-format');

/* ---- Privacy (board 22) ---- */
export interface PrivacyPolicy {
  orgConsent: boolean;
  retentionDays: number;
  policyVersion: number;
  counts: { active: number; promoted: number; purged: number; excluded: number; total: number };
}
export const usePrivacyPolicy = reader<PrivacyPolicy>('cm-privacy', '/clinic-settings/privacy', 30_000);
export const useSavePrivacyPolicy = writer<{ enabled: boolean; retentionDays: number }, PrivacyPolicy>('cm-privacy', '/clinic-settings/privacy');

/* ---- Domains (board 19) — read only; provisioning stays the owner's ---- */
export interface ClinicHost {
  id: string;
  tier: 'shared' | 'subdomain' | 'custom_domain';
  surface: 'emr' | 'patient';
  status: 'active' | 'pending_verification' | 'retired';
  subdomainSlug: string | null;
  customDomain: string | null;
  verificationStatus: string;
  certStatus: string;
}
export interface ClinicDomains {
  slug: string | null;
  displayName: string | null;
  publicHostBase: string;
  patientHostBase: string;
  patientPortalGenericHost: string;
  patientSubdomainsEnabled: boolean;
  customDomainsEnabled: boolean;
  managedDnsEnabled: boolean;
  hosts: ClinicHost[];
  hasEmrSubdomain: boolean;
  hasPatientHost: boolean;
}
export const useClinicDomains = reader<ClinicDomains>('cm-domains', '/clinic-settings/domains', 600_000);

/* ---- Modules (board 26) ---- */
export const useModuleSettings = reader<Record<string, boolean>>('cm-modules', '/clinic-settings/modules');
export const useSaveModuleSettings = writer<Record<string, boolean>, Record<string, boolean>>('cm-modules', '/clinic-settings/modules');

/* ---- Money (board 24) ---- */
export interface MoneyPolicy {
  vatPercent: number | null;
  taxId: string | null;
  etaStatus?: 'not_started' | 'configured' | 'live';
  symbolPosition: 'before' | 'after';
  decimals: 0 | 2;
  discountFlag: 'all' | 'above';
  discountThresholdPercent: number;
  dayCloseReminder: string | null;
  reopenNeedsApproval: boolean;
  payoutShares: Record<string, number>;
}
export const useMoneyPolicy = reader<MoneyPolicy>('cm-money', '/clinic-settings/money');
export const useSaveMoneyPolicy = writer<Partial<MoneyPolicy>, MoneyPolicy>('cm-money', '/clinic-settings/money');
